import { IntervalType } from '@dokucoach/common-types';
import * as yup from 'yup';
import { ICheckboxValue, SuggestionSchema } from '../../../common';

export const MedicationSheetSuggestionSchema = yup.object({
  medications: SuggestionSchema.nullable(),
  schemeMedications: SuggestionSchema.nullable(),
  drugTypes: SuggestionSchema.nullable(),
});

export type IMedicationSheetSuggestion = yup.InferType<typeof MedicationSheetSuggestionSchema>;

export const DoseAndTimeSchema = yup.object({
  amount: yup.string().notRequired().nullable(),
  time: yup.date().notRequired().nullable(),
});

export const DoseAndRequiredTimeWhenTouchedSchema = yup.object({
  amount: yup.string().notRequired().nullable(),
  time: yup.date().when('amount', {
    is: (v: string) => v,
    then: yup.date().required('Angabe erforderlich'),
    otherwise: yup.date().notRequired().nullable(),
  }),
});

export const RequiredDoseAndTimeSchema = yup.object({
  amount: yup.string().required(),
  time: yup.date().required(),
});

export const DoseAndOptionalTimeSchema = yup.object({
  amount: yup.string().required(),
  time: yup.date().notRequired().nullable(),
});

export interface IDoseAndOptionalTime {
  amount: string;
  time?: Date;
}

export interface IDoseAndTime {
  amount: string;
  time: Date;
}

export interface IRequiredDoseAndTime {
  amount: string;
  time: Date;
}

export const CommonMedicationSchema = yup.object({
  residentId: yup.string(),
  remark: yup.string().notRequired().nullable(),
  medicationIngredient: yup.string().required(),
  startDate: yup.date().required(),
  startedAsPrescribed: yup.boolean().required(),
  startedByDoctorId: yup.string().required(),
  isDiscontinued: yup.boolean().required(),
});

export const UpdateCommonMedicationSchema = yup.object({
  id: yup.string().required(),
  residentId: yup.string().notRequired(),
  remark: yup.string().notRequired().nullable(),
  medicationIngredient: yup.string().notRequired(),
  startDate: yup.date().notRequired(),
  startedAsPrescribed: yup.boolean().notRequired(),
  startedByDoctorId: yup.string().notRequired(),
  isDiscontinued: yup.boolean().notRequired(),
});

export interface ICommonMedication {
  residentId: string;
  remark?: string;
  medicationIngredient: string;
  startDate: Date;
  startedAsPrescribed: boolean;
  startedByDoctorId: string;
  isDiscontinued: boolean;
  endDate?: Date;
  endedAsPrescribed?: boolean;
  endedByDoctorId?: string;
}

export interface IUpdateCommonMedication {
  id: string;
  residentId?: string;
  remark?: string;
  medicationIngredient?: string;
  startDate?: Date;
  startedAsPrescribed?: boolean;
  startedByDoctorId?: string;
  isDiscontinued?: boolean;
  endDate?: Date;
  endedAsPrescribed?: boolean;
  endedByDoctorId?: string;
}

export const ConstantMedicationSchema = yup
  .object({
    type: yup.string().required(),
    painMedication: yup.boolean().required(),
    medicationList: yup.boolean().required(),
    withoutAssistance: yup.boolean().required(),
    preMorningMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    morningMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    preNoonMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    noonMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    preEveningMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    eveningMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    preNightMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    nightMedication: DoseAndRequiredTimeWhenTouchedSchema.notRequired().default(undefined),
    intervalNumber: yup.mixed().when('interval', {
      is: (v: string) => v === IntervalType.INTERVAL,
      then: yup.number().required('Angabe erforderlich'),
    }),
    weekdays: yup.mixed().when('interval', {
      is: (v: string) => v === IntervalType.WEEKDAYS,
      then: yup.object().shape({
        value: yup.array().min(1).of(yup.string()).required('Angabe erforderlich'),
      }),
    }),
    interval: yup.string().required(),
    endDate: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.date().required('Angabe erforderlich'),
    }),
    endedAsPrescribed: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.boolean().required('Angabe erforderlich'),
    }),
    endedByDoctorId: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.string().required('Angabe erforderlich'),
    }),
  })
  .concat(CommonMedicationSchema);

export interface IConstantMedication extends ICommonMedication {
  type: string;
  preMorningMedication?: IDoseAndTime;
  morningMedication?: IDoseAndTime;
  preNoonMedication?: IDoseAndTime;
  noonMedication?: IDoseAndTime;
  preEveningMedication?: IDoseAndTime;
  eveningMedication?: IDoseAndTime;
  preNightMedication?: IDoseAndTime;
  nightMedication?: IDoseAndTime;
  intervalNumber: number;
  weekdays: ICheckboxValue;
  interval: IntervalType;
  painMedication: boolean;
  medicationList: boolean;
  withoutAssistance: boolean;
}

export interface IUpdateConstantMedication extends IUpdateCommonMedication {
  type?: string;
  preMorningMedication?: IDoseAndTime;
  morningMedication?: IDoseAndTime;
  preNoonMedication?: IDoseAndTime;
  noonMedication?: IDoseAndTime;
  preEveningMedication?: IDoseAndTime;
  eveningMedication?: IDoseAndTime;
  preNightMedication?: IDoseAndTime;
  nightMedication?: IDoseAndTime;
  intervalNumber?: number;
  weekdays?: ICheckboxValue;
  interval?: string;
  painMedication?: boolean;
  medicationList?: boolean;
  withoutAssistance?: boolean;
}

export const OnDemandMedicationSchema = yup
  .object({
    type: yup.string().required(),
    indication: yup.string().required(),
    singleDosageAmount: yup.string().required(),
    maximumDosageAmount: yup.string().required(),
    painMedication: yup.boolean().required(),
    medicationList: yup.boolean().required(),
    withoutAssistance: yup.boolean().required(),
    endDate: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.date().required('Angabe erforderlich'),
    }),
    endedAsPrescribed: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.boolean().required('Angabe erforderlich'),
    }),
    endedByDoctorId: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.string().required('Angabe erforderlich'),
    }),
  })
  .concat(CommonMedicationSchema);

export const UpdateOnDemandMedicationSchema = yup
  .object({
    type: yup.string().notRequired(),
    indication: yup.string().notRequired(),
    singleDosageAmount: yup.string().notRequired(),
    maximumDosageAmount: yup.string().notRequired(),
    painMedication: yup.boolean().notRequired(),
    medicationList: yup.boolean().notRequired(),
    withoutAssistance: yup.boolean().notRequired(),
    endDate: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.date().required('Angabe erforderlich'),
    }),
    endedAsPrescribed: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.boolean().required('Angabe erforderlich'),
    }),
    endedByDoctorId: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.string().required('Angabe erforderlich'),
    }),
  })
  .concat(UpdateCommonMedicationSchema);

export interface IOnDemandMedication extends ICommonMedication {
  type: string;
  indication: string;
  singleDosageAmount: string;
  maximumDosageAmount: string;
  painMedication: boolean;
  medicationList: boolean;
  withoutAssistance: boolean;
}

export interface IUpdateOnDemandMedication extends IUpdateCommonMedication {
  type?: string;
  indication?: string;
  singleDosageAmount?: string;
  maximumDosageAmount?: string;
  painMedication?: boolean;
  medicationList?: boolean;
  withoutAssistance?: boolean;
}

export const InsulinSchema = yup.object({
  minInterval: yup.number().required(),
  maxInterval: yup.number().required(),
  dose: yup.string().required(),
});

export interface IInsulin {
  minInterval: number;
  maxInterval: number;
  dose: string;
}

export const InsulinMedicationSchema = yup
  .object({
    morningInsulin: DoseAndTimeSchema.notRequired().default(undefined).nullable(),
    morningSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    noonInsulin: DoseAndTimeSchema.notRequired().default(undefined).nullable(),
    noonSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    eveningInsulin: DoseAndTimeSchema.notRequired().default(undefined).nullable(),
    eveningSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    nightInsulin: DoseAndTimeSchema.notRequired().default(undefined).nullable(),
    nightSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    endDate: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.date().required('Angabe erforderlich'),
    }),
    endedAsPrescribed: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.boolean().required('Angabe erforderlich'),
    }),
    endedByDoctorId: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.string().required('Angabe erforderlich'),
    }),
  })
  .concat(CommonMedicationSchema);

export interface IInsulinMedication extends ICommonMedication {
  morningInsulin?: IDoseAndTime;
  noonInsulin?: IDoseAndTime;
  eveningInsulin?: IDoseAndTime;
  nightInsulin?: IDoseAndTime;
  morningSchemas?: IInsulin[];
  noonSchemas?: IInsulin[];
  eveningSchemas?: IInsulin[];
  nightSchemas?: IInsulin[];
}

export const UpdateInsulinMedicationSchema = yup
  .object({
    morningInsulin: DoseAndTimeSchema.notRequired().default(undefined),
    morningSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    noonInsulin: DoseAndTimeSchema.notRequired().default(undefined),
    noonSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    eveningInsulin: DoseAndTimeSchema.notRequired().default(undefined),
    eveningSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    nightInsulin: DoseAndTimeSchema.notRequired().default(undefined),
    nightSchemas: yup.array<IInsulin>(InsulinSchema).notRequired().default([]).nullable(),
    endDate: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.date().required('Angabe erforderlich'),
    }),
    endedAsPrescribed: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.boolean().required('Angabe erforderlich'),
    }),
    endedByDoctorId: yup.mixed().when('isDiscontinued', {
      is: true,
      then: yup.string().required('Angabe erforderlich'),
    }),
  })
  .concat(UpdateCommonMedicationSchema);

export interface IUpdateInsulinMedication extends IUpdateCommonMedication {
  morningInsulin?: IDoseAndTime;
  noonInsulin?: IDoseAndTime;
  eveningInsulin?: IDoseAndTime;
  nightInsulin?: IDoseAndTime;
  morningSchemas?: IInsulin[];
  noonSchemas?: IInsulin[];
  eveningSchemas?: IInsulin[];
  nightSchemas?: IInsulin[];
}

export const PegMedicationSchema = yup.object({
  residentId: yup.string(),
  remark: yup.string().notRequired().nullable(),
  startDate: yup.date().required(),
  startedAsPrescribed: yup.boolean().required(),
  startedByDoctorId: yup.string().required(),
  isDiscontinued: yup.boolean().required(),
  typeOfLiquid: yup.string().required(),
  liquidGivenDate: yup.date().required(),
  calories: yup.number().notRequired().nullable(),
  mlOfLiquid: yup.number().notRequired().nullable(),
  flowRate: yup.string().notRequired().nullable(),
  endDate: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.date().required(),
  }),
  endedAsPrescribed: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.boolean().required(),
  }),
  endedByDoctorId: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.string().required(),
  }),
});

export const UpdatePegMedicationSchema = yup.object({
  id: yup.string().required(),
  residentId: yup.string().notRequired(),
  remark: yup.string().notRequired().nullable(),
  startDate: yup.date().notRequired(),
  startedAsPrescribed: yup.boolean().notRequired(),
  startedByDoctorId: yup.string().notRequired(),
  isDiscontinued: yup.boolean().notRequired(),
  typeOfLiquid: yup.string().notRequired().nullable(),
  liquidGivenDate: yup.date().notRequired().nullable(),
  calories: yup.number().notRequired().nullable(),
  mlOfLiquid: yup.number().notRequired().nullable(),
  flowRate: yup.string().notRequired().nullable(),
  endDate: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.date().required(),
  }),
  endedAsPrescribed: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.boolean().required(),
  }),
  endedByDoctorId: yup.mixed().when('isDiscontinued', {
    is: true,
    then: yup.string().required(),
  }),
});

export interface IPegMedication {
  residentId: string;
  remark?: string;
  startDate?: Date;
  startedAsPrescribed: boolean;
  startedByDoctorId: string;
  isDiscontinued: boolean;
  interval: IntervalType;
  endDate?: Date;
  endedAsPrescribed?: boolean;
  endedByDoctorId?: string;
  typeOfLiquid: string;
  liquidGivenDate: Date;
  calories?: number;
  mlOfLiquid?: number;
  flowRate?: string;
}

export interface IUpdatePegMedication extends Partial<IPegMedication> {
  id: string;
}
